<script lang="ts" setup>
import MRateItem from '~/components/molecules/RateItem.vue'
import MImageLoader from '@/components/molecules/forms/ImageLoader.vue'
import AButton from '~/components/atoms/buttons/Button.vue'
import { ButtonSize, ButtonStyle } from '~/static/enums/components/button'
import RModalPayment from '~/components/organs/modals/PaymentForm.vue'
import RModalCancelSubscribtion from '~/components/organs/modals/CancelSubscribtion.vue'
import RModalLegalInfo from '~/components/organs/modals/LegalInfo.vue'
import RRateItem from '~/components/organs/rates/RateItem.vue'
import { storeToRefs } from 'pinia'
import { useAuth } from '~/store/auth'
import { formatDate } from '~/utils/Helpers'
import { download, generations, months, pluralize } from '~/utils/TextHelper'
import { useRemovalBackgroundStore } from '~/store/removal-background'
import { openModal, closeModal } from '~/utils/Modal'
import { PopupType } from '~/static/enums/popup'
import { PaymentFailCode, TextByError } from '~/static/enums/payment-fail-code'
import { ratesIcons } from '~/static/enums/components/rates-icons'
import {
  getTariffName,
  hasFolderManagement,
  getGenerationsCount,
  getCountMonths,
  SubscriptionDuration,
  SubscriptionTariff,
  getActualCost,
  hasGenerationsStorage,
  canBeObtainedByFree,
} from '~/static/enums/user/subscription'

const authStore = useAuth()
const { isAuth, user, cuncelSubscribtionText, cuncelSubscribtionTextError } = storeToRefs(authStore)

const nextPaymentDate = computed(() => {
  if (user.value?.subscription && !user.value.subscription.is_canceled) {
    const expiredAt = new Date(user.value.subscription.expired_at)
    if (expiredAt.getFullYear() === (new Date()).getFullYear()) {
      return new Intl.DateTimeFormat('ru', {
        day: 'numeric',
        month: 'long',
      }).format(expiredAt)
    }
    return new Intl.DateTimeFormat('ru', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(expiredAt)
  }
  return ''
})

const freeDownloadsDescription = computed(() => {
  if (user.value?.downloads_refresh_at) {
    return 'Осталось ' +
      pluralize(user.value.remaining_downloads, download) +
      ' до ' +
      formatDate(user.value.downloads_refresh_at)
  }
  return ''
})

const freeGenerationsDescription = computed(() => {
  if (user.value?.generations?.remaining_count) {
    return 'Осталось ' +
      pluralize(user.value.generations.remaining_count, generations) +
      ' до ' +
      formatDate(user.value.generations.expired_at)
  }
  return ''
})

const selectedTab = ref(SubscriptionDuration.Month)
const selectTab = (type: SubscriptionDuration) => {
  if (paymentProcess.value) {
    return
  }

  selectedTab.value = type
}

const selectedDuration = computed(() => {
  if (user.value?.subscription) {
    return user.value.subscription.duration
  }
  return selectedTab.value
})

const changeCancelSubscribtionText = (value: string): void => {
  authStore.cuncelSubscribtionText = value
  authStore.cuncelSubscribtionTextError = ''
}

const cancelSubscribtion = async () => {
  if (await authStore.cuncelSubscription()) {
    closeModal()
  }
}

const cancelSubscriptionCancelingReview = (): void => {
  authStore.cuncelSubscribtionText = ''
  authStore.cuncelSubscribtionTextError = ''
  closeModal()
}

const paymentProcess = ref<SubscriptionTariff | false>(false)

const buySubscription = async (tariff: SubscriptionTariff) => {
  if (paymentProcess.value) {
    return
  }

  paymentProcess.value = tariff
  if (user.value?.subscription) {
    buyUpgradeSubscription(tariff)
    return
  }

  const payUrl = await authStore.createSubscription(tariff, selectedDuration.value)
  if (!payUrl) {
    paymentProcess.value = false
    return
  }

  openPaymentModal(
    'Тариф «' + getTariffName(tariff) + '», ' + pluralize(getCountMonths(selectedDuration.value), months),
    payUrl,
    authStore.getUser,
  )
}

const buyUpgradeSubscription = async (tariff: SubscriptionTariff) => {
  const payUrl = await authStore.buyUpgradeSubscription(tariff)
  if (!payUrl) {
    paymentProcess.value = false
    return
  }

  if (payUrl === true) {
    await authStore.getUser()
    paymentProcess.value = false
    return
  }

  openPaymentModal(
    'Переход на тариф «' + getTariffName(tariff) + '», ' + pluralize(
      getCountMonths(selectedDuration.value),
      months,
    ),
    payUrl,
    authStore.getUser,
  )
}

const openPaymentModal = async (
  title: string,
  payUrl: string | false,
  callback: () => void,
) => {
  if (payUrl === false) {
    useNuxtApp().$event('popup:show', {
      type: PopupType.Warning,
      text: '❌ Оплата не прошла. ' + TextByError[PaymentFailCode.TryAgain],
      closeAfter: 3,
    })
  }
  const modal = await openModal(RModalPayment, { url: payUrl, title })
  modal.on('success', () => {
    callback()
    useNuxtApp().$event('popup:show', {
      type: PopupType.Success,
      text: '✅ Успешно оплачено',
      closeAfter: 3,
    })
  })
  modal.on('fail', (code: PaymentFailCode) => {
    useNuxtApp().$event('popup:show', {
      type: PopupType.Warning,
      text: '❌ Оплата не прошла. ' + TextByError[code],
      closeAfter: 3,
    })
  })
  modal.onclose = () => {
    authStore.getUser()
    paymentProcess.value = false
  }
}

const openCancelSubscribtionModal = async () => {
  if (paymentProcess.value) {
    return
  }

  const modal = await openModal(RModalCancelSubscribtion, {
    text: cuncelSubscribtionText,
    textError: cuncelSubscribtionTextError,
  })
  modal.on('change-text', changeCancelSubscribtionText)
  modal.on('cancel-subscribtion', cancelSubscribtion)
  modal.on('cancel', cancelSubscriptionCancelingReview)
}

const imageStore = useRemovalBackgroundStore()
const changeImage = (file: File): void => {
  if (paymentProcess.value) {
    return
  }

  imageStore.loadImageWithBackgroundRemoved(file)
  useRouter().push('/file')
}

const rates = computed(() => {
  interface Rate {
    tariff: SubscriptionTariff
    name: string
    cost: number
    countMonths: number
    canBeObtainedByFree: boolean
    generations?: number
    generationStorage: boolean
    folderManagement: boolean
  }
  const rates: Rate[] = []
  const userTariff = user.value?.subscription?.tariff
  ;[ SubscriptionTariff.Start, SubscriptionTariff.Pro, SubscriptionTariff.Max ].forEach((tariff) => {
    if (!userTariff || tariff > userTariff) {
      rates.push({
        tariff: tariff,
        name: getTariffName(tariff),
        cost: getActualCost(tariff, selectedDuration.value),
        countMonths: getCountMonths(selectedDuration.value),
        canBeObtainedByFree: canBeObtainedByFree(tariff),
        generations: getGenerationsCount(tariff),
        generationStorage: hasGenerationsStorage(tariff),
        folderManagement: hasFolderManagement(tariff),
      })
    }
  })
  return rates
})

</script>

<template>
  <div class="container-lg">
    <div :class="user?.subscription ? 'paid-content' : 'content'">
      <h1 class="mb-32 title">
        Тарифы
      </h1>
      <div
        v-if="!user?.subscription"
        class="overflow-auto"
      >
        <nav
          class="nav nav-tabs"
          role="tablist"
        >
          <div class="d-flex me-3">
            <a
              class="nav-link d-flex align-items-center"
              id="nav-1-m-tab"
              data-bs-toggle="tab"
              href="#nav-rate-1-m"
              role="tab"
              aria-controls="nav-1-m-tab"
              aria-selected="true"
              :class="{ active: selectedTab === SubscriptionDuration.Month }"
              @click.prevent="selectTab(SubscriptionDuration.Month)"
            >Оплата за 1 месяц</a>
            <a
              class="nav-link d-flex align-items-center"
              id="nav-12-m-tab"
              data-bs-toggle="tab"
              href="#nav-rate-12-m"
              role="tab"
              aria-controls="nav-12-m-tab"
              aria-selected="true"
              :class="{ active: selectedTab === SubscriptionDuration.Year }"
              @click.prevent="selectTab(SubscriptionDuration.Year)"
            >за 12 месяцев (скидка 25%)</a>
          </div>
          <div class="legal-text">
            <a
              href="#"
              @click.prevent="openModal(RModalLegalInfo)"
            >
              Оплата для юрлиц
            </a>
          </div>
        </nav>
      </div>
      <div class="tab-content">
        <div class="tab-pane fade show active">
          <div class="row rate-items">
            <div class="col-12 col-md-5 col-xl-3 mb-3 mb-xl-0">
              <MRateItem
                v-if="!user?.subscription"
                name-rate="Бесплатный"
                :cost-by-month="0"
                :downloads="3"
                :generations="5"
                :description-bottom="freeDownloadsDescription"
                :generation-bottom="freeGenerationsDescription"
              >
                <template #button>
                  <MImageLoader
                    v-slot="scope"
                    @change="changeImage"
                  >
                    <div class="bottom-wrap">
                      <AButton
                        :button-style="ButtonStyle.Secondary"
                        :button-size="ButtonSize.Large"
                        class="w-100 btn-rate"
                        @click="scope.loadImage"
                      >
                        Загрузить фото товара
                      </AButton>
                    </div>
                  </MImageLoader>
                </template>
              </MRateItem>
              <MRateItem
                v-else
                :name-rate="getTariffName(user.subscription.tariff)"
                :folder-management="hasFolderManagement(user.subscription.tariff)"
                :generations="getGenerationsCount(user.subscription.tariff)"
                :cost-by-month="user.subscription.price / getCountMonths(user.subscription.duration)"
                :generation-bottom="user.subscription.is_canceled ? '' : 'Следующая оплата ' + nextPaymentDate"
                class="mb-2"
              >
                <template #button>
                  <div
                    v-if="user.subscription.is_canceled"
                    class="paid-canceled-block text-md"
                  >
                    Подписка отменена
                    <br>
                    Тариф действует до
                    {{ formatDate(user.subscription.expired_at) }}
                  </div>
                  <div
                    v-else
                    class="bottom-wrap"
                  >
                    <AButton
                      :button-style="ButtonStyle.Secondary"
                      :button-size="ButtonSize.Large"
                      class="w-100 btn-rate"
                      @click="openCancelSubscribtionModal"
                    >
                      Отменить подписку
                    </AButton>
                  </div>
                </template>
              </MRateItem>
            </div>
            <RRateItem
              v-for="(rate, index) in rates"
              :key="rate.name"
              :name-rate="rate.name"
              :is-auth="isAuth"
              :cost="rate.cost"
              :count-months="rate.countMonths"
              :can-be-obtained-by-free="rate.canBeObtainedByFree"
              :generations="rate.generations"
              :generation-storage="rate.generationStorage"
              :folder-management="rate.folderManagement"
              :has-confirmation-checkbox="!user?.subscription"
              :old-price="user?.subscription?.price"
              :description-bottom="
                user?.subscription && !user.subscription.is_canceled ?
                  'После перехода на новый тариф следующая оплата будет ' + nextPaymentDate : ''
              "
              :payment-process="paymentProcess === rate.tariff"
              @buy="buySubscription(rate.tariff)"
              class="mt-4"
              :class="index === 0 ? 'mt-md-0' : 'mt-xl-0'"
            />
          </div>
        </div>
        <div class="icons-advantages">
          <h2 class="icons-advantages__title">
            Во всех тарифах
          </h2>
          <div class="d-flex flex-wrap flex-column flex-md-row icons-wrap">
            <div
              v-for="(icon, index) in ratesIcons"
              :key="index"
            >
              <component
                :is="icon.icon"
                class="icon-size-32 mb-2"
              />
              <span
                v-html="icon.htmlText"
                class="d-block text-md"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.legal-text {
  font-size: 1rem;
}

.nav-tabs {
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: center;
  min-width: 100%;
  width: max-content;

  .nav-link {
    flex: 0 0 auto;
    color: $indigo-500;

    &.active {
      color: rgba(73, 80, 87, 1);
    }
  }
}

.paid-canceled-block {
  border-top: 1px solid #dee2e6;
  padding: rem(8px) 0;
  text-align: center;
  margin: 0 rem(15px);
}

.tab-content {
  display: flex;
  flex-direction: column;
  margin-top: rem(24px);
}

.icons-advantages {
  max-width: 1116px;

  &__title {
    font-size: rem(24px);
    line-height: 1.2;
    margin-bottom: rem(24px);

    @include media-breakpoint-down(xl) {
      margin-bottom: rem(32px);
    }
  }
}

.icons-wrap {
  gap: rem(20px) rem(40px);

  @include media-breakpoint-up(md) {
    gap: rem(32px) rem(40px);
  }
}

.title {
  @include media-breakpoint-down(md) {
    font-size: rem(32px);
  }
}

.rate-items {
  margin-bottom: rem(24px);
  position: relative;

  @include media-breakpoint-up(xl) {
    margin-bottom: rem(32px);

    .fake-checkbox {
      height: rem(34px);
    }
  }
}

.btn-rate {
  border-radius: rem(4px);

  @include media-breakpoint-down(md) {
    font-size: rem(16px);
  }
}

.bottom-wrap {
  margin: 0 rem(-1px) rem(-1px);

  .btn-rate {
    font-size: rem(16px);
    line-height: 1.5;
    font-weight: 400;
  }
}

.content {
  margin: rem(80px) 0;

  @include media-breakpoint-down(md) {
    margin: rem(24px) 0 rem(64px);
  }
}

.paid-content {
  margin: rem(100px) 0 rem(80px);

  @include media-breakpoint-down(lg) {
    margin: rem(80px) 0;
  }

  @include media-breakpoint-down(md) {
    margin: rem(24px) 0 rem(64px);
  }

  .title {
    @include media-breakpoint-down(md) {
      font-size: rem(32px);
    }
  }

  .icons-wrap {
    & > div {
      margin: 0 rem(32px) rem(32px) 0;

      @include media-breakpoint-down(xl) {
        width: calc(50% - 32px);
      }

      @include media-breakpoint-down(lg) {
        &:nth-child(1), &:nth-child(2), &:nth-child(3), &:nth-child(4) {
          width: calc(50% - 32px);
        }

        &:nth-child(5), &:nth-child(6), &:nth-child(7) {
          width: calc(100% - 32px);
        }
      }
    }
  }

  .rate-item {
    height: fit-content;
  }
}

.icon-subscription-parent {
  position: relative;

  @include media-breakpoint-up(md) {
    height: 100%;
  }
}

.icon-subscription {
  position: absolute;
  top: -16px;
  left: 47%;
  color: $indigo-500;
}

.additional-generations {
  border: 1px solid $gray-300;
  border-radius: rem(4px);
  padding: rem(16px) rem(24px) rem(18px);
  margin-bottom: rem(40px);
}
</style>
